import type { CalcInputs, StorageTier } from '@/lib/pricing'
import { fmtNumber } from '@/lib/pricing'
import Slider from './Slider'
import {Cpu, MemoryStick, Clock, HardDrive, Network, Zap, Snowflake, Archive, Database} from 'lucide-react'

interface Props {
  inputs: CalcInputs
  onChange: (next: CalcInputs) => void
}

const TIERS: { id: StorageTier, label: string, sub: string, icon: any }[] = [
  { id: 'standard', label: 'Standard', sub: 'Hot · ms access', icon: Database },
  { id: 'cold', label: 'Cold', sub: 'IA · seconds', icon: Snowflake },
  { id: 'archive', label: 'Archive', sub: 'Glacier · hours', icon: Archive },
]

function CalculatorPanel({ inputs, onChange }: Props) {
  const update = (patch: Partial<CalcInputs>) => onChange({ ...inputs, ...patch })

  return (
    <div className="rounded-2xl border border-[#1F2A44] bg-[#0E1525]/80 backdrop-blur-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-[#1F2A44] flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 pulse-ring"></div>
          <h2 className="text-sm font-semibold text-slate-200 tracking-tight">Workload Profile</h2>
        </div>
        <button
          onClick={() => onChange({
            cpuCores: 16,
            ramGb: 64,
            computeHours: 730,
            storageGb: 2000,
            storageTier: 'standard',
            egressGb: 5000,
            invocations: 2_000_000,
          })}
          className="text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-cyan-300 transition-colors"
        >
          Reset
        </button>
      </div>

      <div className="p-5 space-y-7">
        {/* Compute Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-cyan-400">Compute</span>
            <div className="flex-1 h-px bg-gradient-to-r from-cyan-500/20 to-transparent"></div>
          </div>
          <div className="space-y-5">
            <Slider
              icon={<Cpu className="w-3.5 h-3.5" />}
              label="vCPU Cores"
              sublabel="EC2 / VM / Compute Engine"
              min={2} max={128} step={1}
              value={inputs.cpuCores}
              onChange={v => update({ cpuCores: v })}
            />
            <Slider
              icon={<MemoryStick className="w-3.5 h-3.5" />}
              label="RAM"
              sublabel="Aggregate memory"
              min={4} max={512} step={4} unit="GB"
              value={inputs.ramGb}
              onChange={v => update({ ramGb: v })}
            />
            <Slider
              icon={<Clock className="w-3.5 h-3.5" />}
              label="Active hours / month"
              sublabel="730 = always-on"
              min={1} max={730} step={1} unit="hrs"
              value={inputs.computeHours}
              onChange={v => update({ computeHours: v })}
            />
          </div>
        </div>

        {/* Storage Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-400">Storage</span>
            <div className="flex-1 h-px bg-gradient-to-r from-emerald-500/20 to-transparent"></div>
          </div>
          <div className="space-y-5">
            <Slider
              icon={<HardDrive className="w-3.5 h-3.5" />}
              label="Block / Object volume"
              sublabel="S3 · Blob · Cloud Storage"
              min={10} max={50_000} step={10}
              logScale
              value={inputs.storageGb}
              formatValue={v => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)} TB` : `${Math.round(v)} GB`}
              onChange={v => update({ storageGb: v })}
            />
            <div>
              <div className="text-[13px] font-semibold text-slate-200 tracking-tight mb-2.5">Storage tier</div>
              <div className="grid grid-cols-3 gap-2">
                {TIERS.map(t => {
                  const active = inputs.storageTier === t.id
                  return (
                    <button
                      key={t.id}
                      onClick={() => update({ storageTier: t.id })}
                      className={`group relative px-2.5 py-3 rounded-lg border text-left transition-all ${
                        active
                          ? 'border-emerald-500/50 bg-emerald-500/[0.06]'
                          : 'border-[#1F2A44] hover:border-[#2A3656] bg-[#0B0F1C]/40'
                      }`}
                    >
                      <t.icon className={`w-3.5 h-3.5 mb-1.5 ${active ? 'text-emerald-300' : 'text-slate-500'}`} />
                      <div className={`text-[12px] font-semibold ${active ? 'text-slate-100' : 'text-slate-300'}`}>{t.label}</div>
                      <div className="text-[10px] font-mono text-slate-500 mt-0.5">{t.sub}</div>
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Network + Serverless */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-400">Network & Serverless</span>
            <div className="flex-1 h-px bg-gradient-to-r from-amber-500/20 to-transparent"></div>
          </div>
          <div className="space-y-5">
            <Slider
              icon={<Network className="w-3.5 h-3.5" />}
              label="Egress bandwidth"
              sublabel="Outbound internet transfer"
              min={10} max={100_000} step={10}
              logScale
              value={inputs.egressGb}
              formatValue={v => v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)} TB` : `${Math.round(v)} GB`}
              onChange={v => update({ egressGb: v })}
            />
            <Slider
              icon={<Zap className="w-3.5 h-3.5" />}
              label="Function invocations / mo"
              sublabel="Lambda · Functions · Cloud Run"
              min={0} max={100_000_000} step={10_000}
              logScale
              value={inputs.invocations}
              formatValue={v => fmtNumber(Math.round(v))}
              onChange={v => update({ invocations: v })}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default CalculatorPanel
